import classnames from 'classnames'
import { useField } from 'formik'
import { type SelectComponents } from 'node_modules/react-select/dist/declarations/src/components'
import * as React from 'react'
import { type GroupBase, type InputActionMeta } from 'react-select'
import { useTranslatable } from 'src/hooks/locale/utils'
import Select from '../../Select'
import { useFormData } from './Form'

export interface Option {
  readonly label: string
  readonly value: string
  readonly options?: readonly { readonly label: string; readonly value: string }[]
}

function flattenSelectOptions(options: readonly Option[]): Array<{ readonly label: string; readonly value: string }> {
  const flattened: Array<{ readonly label: string; readonly value: string }> = []
  for (const option of options) {
    if (option.options != null && option.options.length > 0) {
      flattened.push(...option.options)
    } else {
      flattened.push(option)
    }
  }
  return flattened
}

interface Props {
  readonly options: readonly Option[] | null
  readonly placeholder?: string
  readonly className?: string
  readonly label?: string
  readonly name: string
  readonly required?: boolean
  readonly disabled?: boolean
  readonly isLoading?: boolean
  /** When false, the control stays enabled while loading (keeps search text and menu open for async search). Default true. */
  readonly disableWhileLoading?: boolean
  readonly isMulti?: boolean
  readonly labelAnimation?: boolean
  readonly components?: Partial<SelectComponents<any, boolean, GroupBase<any>>> | undefined
  readonly instructions?: React.ReactElement
  readonly closeMenuOnSelect?: boolean
  readonly hideSelectedOptions?: boolean
  readonly menuPortalTarget?: HTMLElement
  readonly getOptionLabel?: (option: { value: string; label: string }) => string
  readonly getOptionValue?: (option: { value: string; label: string }) => string
  /** When true with isMulti: store only option values (string[]) for URL/API-friendly serialization. Default: store full option objects. */
  readonly storeValueOnly?: boolean
  /** When set with isMulti: option with this value acts as "select all" – choosing it selects all other options. */
  readonly selectAllOptionValue?: string
  readonly onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void
  /** When true, do not filter options by the control input (use when options come from a server search). */
  readonly matchFromServerOnly?: boolean
  readonly onMenuOpen?: () => void
  readonly onMenuScrollToBottom?: (event: WheelEvent | TouchEvent) => void
}

export default function SelectInput({
  label,
  labelAnimation = false,
  components,
  instructions,
  required,
  closeMenuOnSelect = true,
  hideSelectedOptions = false,
  menuPortalTarget = document.body,
  getOptionLabel = undefined,
  getOptionValue = undefined,
  storeValueOnly = false,
  selectAllOptionValue,
  onInputChange,
  matchFromServerOnly = false,
  disableWhileLoading = true,
  onMenuOpen,
  onMenuScrollToBottom,
  ...props
}: Props): React.ReactElement {
  const t = useTranslatable()
  const [field, meta, helpers] = useField({
    ...props,
    validate(value) {
      if (required === true) {
        if (value == null || value === '' || value === undefined || ((props.isMulti ?? false) && value[0] == null)) {
          return label !== undefined ? `${label} ${t('common:is_required')}` : t('common:field_is_required')
        }
      }
    },
  })

  const { setValue, setTouched } = helpers
  const [formData] = useFormData()
  const errors = formData.errors.fields[props.name] ?? []
  const isInvalid = (errors.length > 0 || meta.error != null) && meta.touched ? '!border-primaryRed' : ''
  const [focused, setFocused] = React.useState(false)

  const handleChange = React.useCallback(
    (option: { value: string } | readonly { value: string }[] | null) => {
      if (props.isMulti ?? false) {
        const arr = option == null ? [] : Array.isArray(option) ? option : [option]
        const hasSelectAll =
          selectAllOptionValue != null && arr.some((o) => (o as { value: string }).value === selectAllOptionValue)
        if (hasSelectAll && props.options != null) {
          const allExceptSelectAll = flattenSelectOptions(props.options).filter((o) => o.value !== selectAllOptionValue)
          void setValue(storeValueOnly ? allExceptSelectAll.map((o) => o.value) : allExceptSelectAll)
        } else {
          const filtered = arr.filter((o) => (o as { value: string }).value !== selectAllOptionValue)
          void setValue(storeValueOnly ? filtered.map((o) => (o as { value: string }).value) : filtered)
        }
      } else {
        void setValue((option as { value: string } | null)?.value)
      }
    },
    [props.isMulti, props.options, storeValueOnly, selectAllOptionValue, setValue]
  )

  const getValue = (): any => {
    if (props.options != null) {
      const leafOptions = flattenSelectOptions(props.options)
      if (props.isMulti ?? false) {
        if (storeValueOnly) {
          const values = Array.isArray(field.value) ? field.value : field.value ? [field.value] : []
          return leafOptions.filter((option) => values.includes(option.value))
        }
        return Array.isArray(field.value) ? field.value : []
      }
      const fv = field.value
      return (
        leafOptions.find((option) => {
          if (fv == null || fv === '') {
            return option.value === ''
          }
          return String(option.value) === String(fv)
        }) ?? null
      )
    }
    return (props.isMulti ?? false) ? [] : ('' as any)
  }

  return (
    <div className="mb-3 w-full">
      <div
        className={classnames('relative w-full', {
          labelAnimation,
        })}
        data-testid={props.name}
      >
        {label != null && (
          <label
            className={`${
              labelAnimation
                ? `absolute left-[12px] z-10 flex-nowrap text-lightPrimaryIconText duration-200 ease-in-out ${
                    (field.value != null && field.value !== '') || focused
                      ? 'top-[5%] translate-y-[-10%] text-[11px]'
                      : 'top-1/2 -translate-y-1/2'
                  }`
                : 'mb-[10px] flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor'
            }`}
            htmlFor={props.name}
          >
            {label}{' '}
            {required != null && (
              <span className="pl-1 text-lightSecondaryWarning" data-testid={`${label ?? props.placeholder}.required`}>
                *
              </span>
            )}{' '}
            {instructions}
          </label>
        )}
        <Select
          placeholder={
            props.placeholder != null && !labelAnimation
              ? props.placeholder
              : props.placeholder == null && !labelAnimation
                ? t('common:select')
                : ''
          }
          className={`p-0 ${isInvalid} ${props.className}`}
          options={props.options!}
          name={field.name}
          // defaultValue={props.options}
          value={getValue()}
          onChange={(option) => handleChange(option as { value: string })}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            void setTouched(true, true)
            setFocused(false)
          }}
          instanceId={props.name}
          hasError={
            meta.error != null &&
            meta.touched &&
            (required === true
              ? (Array.isArray(field.value) && field.value.length === 0) ||
                field.value === '' ||
                field.value === undefined
              : true)
          }
          inputId={props.name}
          isDisabled={props.disabled === true || (disableWhileLoading && Boolean(props.isLoading))}
          noOptionsMessage={() => t('common:no_options')}
          isLoading={props.isLoading}
          isMulti={props.isMulti}
          components={components}
          closeMenuOnSelect={closeMenuOnSelect}
          hideSelectedOptions={hideSelectedOptions}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          {...(onInputChange != null && { onInputChange })}
          {...(matchFromServerOnly && { filterOption: (): boolean => true })}
          {...(menuPortalTarget != null && { menuPortalTarget })}
          {...(onMenuOpen != null && { onMenuOpen })}
          {...(onMenuScrollToBottom != null && { onMenuScrollToBottom })}
        />
      </div>
      {meta.error != null &&
      meta.touched &&
      (required === true
        ? (Array.isArray(field.value) && field.value.length === 0) || field.value === '' || field.value === undefined
        : true) ? (
        <div className="mt-2 text-[13px] text-errorMessages">{meta.error}</div>
      ) : null}
      {errors.map((error, i) => (
        <div key={i} className="mt-2 text-[13px] text-errorMessages">
          {error}
        </div>
      ))}
    </div>
  )
}
